/**
 * Rasterises a UV insert face to a `File` so the assembled card can be shown on the cart line.
 *
 * The card only ever existed as live DOM inside the personalisation journey: once the customer added
 * to bag, nobody saw it again. The print partner does not need this image — the print is driven
 * entirely by the text line item properties and the template id — so this is a *representation* of
 * the card, not a source of truth. It is drawn from the same parsed `UVInsertConfig` the DOM
 * renderer (`DevFaceVisualisers`) reads, so the two cannot drift apart structurally, but it does not
 * attempt to reproduce the browser's text layout exactly.
 *
 * Nothing here may ever block or break add to bag: every failure path returns `null` and the caller
 * simply omits the preview.
 */

import {
  isUvInsertTextListElement,
  type UVInsertConfig,
  type UVInsertFaceElement,
  type UVInsertFaceElementConfigData,
  type UVInsertFaceElementTextListConfig,
  type UVInsertFaceElementType,
} from "@shared/models/uv-inserts"
import { getBirthstoneStarPoints } from "@shared/view/uv-inserts/birthstone-star"
import { parseCssLength, resolveFontSizePx } from "@shared/view/uv-inserts/font-size"
import { capitaliseWords } from "@shared/view/uv-inserts/capitalise-words"
import { getTextBoxPaddingEm } from "@shared/view/uv-inserts/text-box-padding"
import {
  getUvInsertTextListRows,
  type UvInsertTextListRow,
} from "@shared/view/uv-inserts/text-list-rows"
import { getUvInsertTextListRuleMetrics } from "@shared/view/uv-inserts/text-list-rule"
import {
  getUvInsertTextListMomentMargin,
  getUvInsertTextListStarMetrics,
} from "@shared/view/uv-inserts/text-list-star"

import { ensureFaceFontsLoaded } from "./ensureFaceFontsLoaded"

/**
 * Face sizes in CSS pixels, mirroring `visualiser.module.css`.
 *
 * The DOM face is a fixed-size box (min and max are equal for every type), so the preview can be
 * laid out against the same coordinate space the config's `positioning`/`size` values are written
 * in, then scaled up once at draw time.
 */
export const UV_INSERT_FACE_SIZES: Record<UVInsertFaceElementType, { width: number; height: number }> = {
  INSERT: { width: 315, height: 315 },
  GREETINGS_CARD: { width: 215, height: 301 },
  LANDSCAPE_INSERT: { width: 315, height: 210 },
}

/** `visualiser-text-box.module.css` centres text; only an explicit `style.textAlign` overrides it. */
const DEFAULT_TEXT_ALIGN = "center"

/** The default floor of the DOM auto-shrink loop in `VisualiserTextBox.vm`; an element's `minFontSize` overrides it. */
const MIN_SHRUNK_FONT_PX = 8

/** `.visualiser-text-list__row` sets `gap: 0.35em` between the bullet and the month/moment column. */
const TEXT_LIST_BULLET_GAP_EM = 0.35

/** Default rasterised width. 3x the 315 px face: crisp in the cart thumbnail and in its lightbox. */
const DEFAULT_OUTPUT_WIDTH = 945

/** JPEG quality for the encoded preview — the same setting `cropImageToView` uses. */
const OUTPUT_QUALITY = 0.9

// ---------------------------------------------------------------------------
// Pure layout helpers (unit tested without a canvas)
// ---------------------------------------------------------------------------

export { parseCssLength, resolveFontSizePx }

/** A resolved element rectangle in face CSS pixels. */
export type ElementBox = {
  left: number
  top: number
  width: number
  /** Only resolvable when the config gives a `height`; TEXT boxes auto-size in the DOM. */
  height: number | null
  /** The ceiling the DOM's auto-shrink honours (`size.height` wins over `size.maxHeight`). */
  maxHeight: number | null
}

/**
 * Resolves one `elementConfig` entry's absolute box against the face.
 *
 * Mirrors what the visualiser view models hand to CSS: `position: absolute` with the config's
 * `top`/`left`/`right`/`bottom`, and a width from `size.width` clamped by `minWidth`/`maxWidth`.
 * A missing `left` is derived from `right` + width so a right-anchored element still lands; a
 * missing width fills from `left` to `right` (or to the face edge), exactly as a block box does.
 *
 * @param options - Options object.
 * @param options.size - The element's `size` config.
 * @param options.positioning - The element's `positioning` config.
 * @param options.faceWidth - Face width in CSS pixels.
 * @param options.faceHeight - Face height in CSS pixels.
 * @returns The element box in face CSS pixels.
 */
export function resolveElementBox({
  size,
  positioning,
  faceWidth,
  faceHeight,
}: {
  size?: Record<string, string | number | undefined>
  positioning?: Record<string, string | undefined>
  faceWidth: number
  faceHeight: number
}): ElementBox {
  const left = parseCssLength(positioning?.left, faceWidth)
  const right = parseCssLength(positioning?.right, faceWidth)
  const top = parseCssLength(positioning?.top, faceHeight)
  const bottom = parseCssLength(positioning?.bottom, faceHeight)

  const declaredWidth = parseCssLength(size?.width, faceWidth)
  const declaredHeight = parseCssLength(size?.height, faceHeight)
  const minWidth = parseCssLength(size?.minWidth, faceWidth)
  const maxWidth = parseCssLength(size?.maxWidth, faceWidth)

  let resolvedWidth = declaredWidth
  if (resolvedWidth == null) {
    if (left != null && right != null) resolvedWidth = faceWidth - left - right
    else resolvedWidth = faceWidth - (left ?? 0)
  }
  if (maxWidth != null) resolvedWidth = Math.min(resolvedWidth, maxWidth)
  if (minWidth != null) resolvedWidth = Math.max(resolvedWidth, minWidth)
  resolvedWidth = Math.max(0, resolvedWidth)

  let resolvedLeft = left
  if (resolvedLeft == null) {
    resolvedLeft = right != null ? faceWidth - right - resolvedWidth : 0
  }

  let resolvedTop = top
  if (resolvedTop == null) {
    resolvedTop = bottom != null && declaredHeight != null ? faceHeight - bottom - declaredHeight : 0
  }

  // `buildUvInsertTextVisualiserSize` makes a fixed `height` drive both min and max; the ceiling the
  // shrink loop reads is therefore `height` when present, `maxHeight` otherwise.
  const maxHeight = declaredHeight ?? parseCssLength(size?.maxHeight, faceHeight)

  return {
    left: resolvedLeft,
    top: resolvedTop,
    width: resolvedWidth,
    height: declaredHeight,
    maxHeight,
  }
}

/**
 * Greedily wraps text to a pixel width, honouring explicit newlines.
 *
 * Deliberately simple: it breaks on spaces only, and a single word wider than the box is left to
 * overflow rather than being hyphenated or character-split. The browser's line breaking differs in
 * the details, and matching it exactly is explicitly out of scope — the preview only has to read as
 * the customer's card. A TEXT element opts into `breakLongWords` to mirror its textarea's
 * `overflow-wrap: break-word`; the timeline list does not, and keeps clipping an overlong word.
 *
 * @param options - Options object.
 * @param options.text - The text to wrap.
 * @param options.maxWidth - The available width in CSS pixels.
 * @param options.measure - Measures a string's width, e.g. `(s) => ctx.measureText(s).width`.
 * @param options.breakLongWords - Splits a word wider than `maxWidth` across lines, character by character.
 * @returns One entry per rendered line; an empty input yields no lines.
 */
export function wrapText({
  text,
  maxWidth,
  measure,
  breakLongWords = false,
}: {
  text: string
  maxWidth: number
  measure: (text: string) => number
  breakLongWords?: boolean
}): string[] {
  if (!text) return []
  const lines: string[] = []

  for (const paragraph of text.split("\n")) {
    if (!paragraph.trim()) {
      // A blank line in the poem is a deliberate stanza break, so it keeps its own line box.
      lines.push("")
      continue
    }
    if (!(maxWidth > 0)) {
      lines.push(paragraph)
      continue
    }

    let current = ""
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      if (breakLongWords && measure(word) > maxWidth) {
        // Like the textarea: the word starts on a fresh line, then breaks wherever it runs out of room.
        if (current) lines.push(current)
        current = ""
        for (const character of word) {
          if (current && measure(current + character) > maxWidth) {
            lines.push(current)
            current = character
          } else {
            current += character
          }
        }
        continue
      }
      const candidate = current ? `${current} ${word}` : word
      if (current && measure(candidate) > maxWidth) {
        lines.push(current)
        current = word
      } else {
        current = candidate
      }
    }
    if (current) lines.push(current)
  }

  return lines
}

/**
 * Shrinks a font size until the wrapped text fits its box, mirroring `VisualiserTextBox.vm`.
 *
 * The DOM steps down in half pixels and stops at the element's `minFontSize`, 8 px by default; a font
 * already smaller than that floor never shrinks at all (the poem is set at 4.5 px, and must stay
 * there). The 6.33 px Story of Us caption sets a lower floor so long text can shrink (ALBAU-950).
 * `maxLines` and `maxHeight` both cap the box and the tighter of the two wins, as in `layoutTextElement`.
 *
 * @param options - Options object.
 * @param options.fontSize - The configured size in CSS pixels.
 * @param options.minFontSize - The shrink floor in CSS pixels, or undefined for 8.
 * @param options.lineHeight - The line-height multiplier.
 * @param options.maxHeight - The box's height ceiling, or null when unbounded.
 * @param options.maxLines - The configured line cap, or undefined.
 * @param options.countLines - Returns how many lines the text wraps to at a given font size.
 * @returns The font size to draw at.
 */
export function shrinkFontSizeToFit({
  fontSize,
  minFontSize = MIN_SHRUNK_FONT_PX,
  lineHeight,
  maxHeight,
  maxLines,
  countLines,
  verticalPaddingEm = 0,
}: {
  fontSize: number
  minFontSize?: number
  lineHeight: number
  maxHeight: number | null
  maxLines?: number
  countLines: (fontSize: number) => number
  /**
   * Top + bottom padding in em (Amalfi Coast's 1.0em; 0 for everything else). The DOM's
   * `scrollHeight` includes the textarea's padding and `computeEffectiveMaxHeight` adds it to the
   * `maxLines` cap, so both sides of the comparison carry it here too — or the canvas would shrink a
   * script font one step further than the face does. It scales with the candidate size because
   * the padding is em.
   */
  verticalPaddingEm?: number
}): number {
  let ceiling = maxHeight
  if (maxLines) {
    const maxLinesHeight = maxLines * lineHeight * fontSize + verticalPaddingEm * fontSize
    ceiling = ceiling == null ? maxLinesHeight : Math.min(ceiling, maxLinesHeight)
  }
  if (ceiling == null || !(ceiling > 0)) return fontSize

  const fits = (size: number) => countLines(size) * lineHeight * size + verticalPaddingEm * size <= ceiling
  if (fits(fontSize)) return fontSize

  let candidate = fontSize
  while (candidate - 0.5 >= minFontSize) {
    candidate -= 0.5
    if (fits(candidate)) return candidate
  }
  // The floor is a floor, not a minimum: a font configured below it (the 4.5 px poem) keeps its
  // configured size, exactly as the DOM's `while (testFontSize > minFontSize)` loop leaves it.
  return Math.min(fontSize, Math.max(minFontSize, candidate))
}

/** One `TEXT_LIST` row the preview draws. The DOM list renders exactly these rows. */
export type CardPreviewTextListRow = UvInsertTextListRow

/**
 * The `TEXT_LIST` rows to draw, in order.
 *
 * A thin wrapper over the builder the DOM list reads (`getUvInsertTextListRows`) — the two
 * renderers used to keep a copy each and drifted. Months, years and moments are trimmed, a row
 * without a month is dropped entirely rather than left as a gap, and a year that is not four
 * digits is treated as absent.
 *
 * @param options - Options object.
 * @param options.element - The `TEXT_LIST` element config.
 * @param options.textByKey - The host-populated text.
 * @returns The rows to draw.
 */
export function getCardPreviewTextListRows({
  element,
  textByKey,
}: {
  element: UVInsertFaceElementTextListConfig
  textByKey: Record<string, string>
}): CardPreviewTextListRow[] {
  return getUvInsertTextListRows({ element, textByKey })
}

/** A source rectangle to hand to `drawImage`, plus the destination it fills. */
export type CoverFitRect = {
  sx: number
  sy: number
  sw: number
  sh: number
}

/**
 * The source rectangle that reproduces CSS `object-fit: cover` for a box.
 *
 * `VisualiserImageArea` paints its `<img>` at `width/height: 100%` with `object-fit: cover`, so the
 * largest centred crop of the source with the box's aspect ratio is what the customer sees. A framed
 * photo has already been rasterised to the window's aspect by `cropImageToView`, so cover-fitting it
 * is a no-op and the preview matches exactly what prints; an unframed upload gets the same centred
 * crop the DOM shows.
 *
 * @param options - Natural size of the image and the destination box size.
 * @returns The source rectangle, or null when any dimension is degenerate.
 */
export function computeCoverFitRect({
  naturalWidth,
  naturalHeight,
  boxWidth,
  boxHeight,
}: {
  naturalWidth: number
  naturalHeight: number
  boxWidth: number
  boxHeight: number
}): CoverFitRect | null {
  if (
    !Number.isFinite(naturalWidth) ||
    !Number.isFinite(naturalHeight) ||
    !Number.isFinite(boxWidth) ||
    !Number.isFinite(boxHeight) ||
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    boxWidth <= 0 ||
    boxHeight <= 0
  ) {
    return null
  }

  const sourceAspect = naturalWidth / naturalHeight
  const boxAspect = boxWidth / boxHeight

  if (sourceAspect > boxAspect) {
    // Source is wider than the box: trim the sides.
    const sw = naturalHeight * boxAspect
    return { sx: (naturalWidth - sw) / 2, sy: 0, sw, sh: naturalHeight }
  }
  // Source is taller than the box (or the same shape): trim top and bottom.
  const sh = naturalWidth / boxAspect
  return { sx: 0, sy: (naturalHeight - sh) / 2, sw: naturalWidth, sh }
}

/** A solid CSS border parsed off an element's `style`. */
export type ParsedBorder = { width: number; color: string }

/**
 * Reads a `width solid color` shorthand off an element's `style` config.
 *
 * The polaroid's white frame is expressed this way (`border: '5px solid #ffffff'`), and CSS draws it
 * *outside* the `content-box` the image fills, so the preview has to inflate the box to match.
 * Anything more exotic than a solid pixel border is ignored rather than guessed at.
 *
 * @param border - The raw `style.border` value.
 * @returns The parsed border, or null when there is none to draw.
 */
export function parseSolidBorder(border: string | number | null | undefined): ParsedBorder | null {
  if (typeof border !== "string") return null
  const match = /^\s*(\d*\.?\d+)px\s+solid\s+(.+?)\s*$/i.exec(border)
  if (!match) return null
  const width = Number.parseFloat(match[1])
  if (!Number.isFinite(width) || width <= 0) return null
  return { width, color: match[2] }
}

/**
 * Whether there is anything of the customer's on the card worth showing.
 *
 * A card with no months, no photo and no caption is the blank template: rasterising it would put an
 * empty picture on the cart line and cost an encode for nothing.
 *
 * @param options - Options object.
 * @param options.textByKey - The slot's text state.
 * @param options.imageByKey - The slot's image state.
 * @returns True when at least one text value or image is populated.
 */
export function hasCardPreviewContent({
  textByKey,
  imageByKey,
}: {
  textByKey: Record<string, string>
  imageByKey: Record<string, File | null>
}): boolean {
  for (const value of Object.values(textByKey ?? {})) {
    if (typeof value === "string" && value.trim() !== "") return true
  }
  for (const file of Object.values(imageByKey ?? {})) {
    if (file instanceof File) return true
  }
  return false
}

/**
 * Builds a canvas `font` shorthand, quoting family names that need it.
 *
 * @param options - Options object.
 * @param options.font - The configured family (e.g. `Amalfi Coast`).
 * @param options.fontSize - Size in CSS pixels.
 * @param options.fontWeight - Optional weight.
 * @param options.fontStyle - Optional style (`italic`).
 * @returns The `font` shorthand.
 */
export function buildCanvasFont({
  font,
  fontSize,
  fontWeight,
  fontStyle,
}: {
  font: string
  fontSize: number
  fontWeight?: string | number
  fontStyle?: string
}): string {
  const family = /[\s"']/.test(font) ? `"${font.replace(/"/g, '\\"')}"` : font
  const parts: string[] = []
  if (fontStyle) parts.push(fontStyle)
  if (fontWeight) parts.push(String(fontWeight))
  parts.push(`${fontSize}px`)
  parts.push(`${family}, sans-serif`)
  return parts.join(" ")
}

// ---------------------------------------------------------------------------
// Canvas rendering
// ---------------------------------------------------------------------------

/** `var(--color-x)` cannot be resolved without a live element, so the few the configs use are mapped. */
const CSS_VARIABLE_FALLBACKS: Record<string, string> = {
  "--color-white": "#ffffff",
  "--color-black": "#000000",
}

/**
 * Resolves a config colour to something canvas accepts.
 *
 * @param value - The raw colour.
 * @param fallback - Used when the value is missing or an unmapped CSS variable.
 * @returns A canvas-safe colour string.
 */
function resolveColor(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback
  const variable = /^var\(\s*(--[\w-]+)\s*(?:,\s*([^)]*))?\)$/.exec(value.trim())
  if (!variable) return value
  const mapped = CSS_VARIABLE_FALLBACKS[variable[1]]
  if (mapped) return mapped
  const declaredFallback = variable[2]?.trim()
  return declaredFallback ? resolveColor(declaredFallback, fallback) : fallback
}

/** Decodes a `File` into an image, revoking its object URL once drawing is done. */
function loadImageFromFile(file: File): Promise<{ image: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => resolve({ image, url })
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(`renderCardPreview: could not decode ${file.name}`))
    }
    image.src = url
  })
}

/**
 * Decodes a same- or cross-origin URL into an image.
 *
 * `crossOrigin` is set so a Shopify CDN variant image does not taint the canvas; without it
 * `toBlob` would throw a `SecurityError` and lose the whole preview over a background.
 */
function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = "anonymous"
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`renderCardPreview: could not load ${url}`))
    image.src = url
  })
}

/** Per-element drawing context shared by the element painters. */
export type DrawContext = {
  ctx: CanvasRenderingContext2D
  faceWidth: number
  faceHeight: number
  textByKey: Record<string, string>
  imageByKey: Record<string, File | null>
}

/**
 * Sets canvas letter spacing where the browser supports it.
 *
 * `letterSpacing` is part of the canvas drawing state, so it is saved and restored with everything
 * else — but it is also Chromium-only today, and on a browser without it the tracked title simply
 * comes out a hair narrower.
 *
 * @param options - Options object.
 * @param options.ctx - The 2d context.
 * @param options.letterSpacing - A CSS length, e.g. `"1.6px"` or `"0px"`.
 */
function setCanvasLetterSpacing({
  ctx,
  letterSpacing,
}: {
  ctx: CanvasRenderingContext2D
  letterSpacing: string
}): void {
  const spaced = ctx as CanvasRenderingContext2D & { letterSpacing?: string }
  if ("letterSpacing" in spaced) spaced.letterSpacing = letterSpacing
}

/** The text a TEXT element actually shows, mirroring `DevFaceVisualisers`' three branches. */
function resolveTextValue({
  element,
  textByKey,
}: {
  element: Extract<UVInsertFaceElementConfigData, { type: "TEXT" }>
  textByKey: Record<string, string>
}): string {
  const key = element.lineItemPropertyKey?.trim() ?? ""
  let value: string
  if (!key) value = element.value ?? ""
  else {
    const stored = textByKey[key]
    if (element.editable === false) {
      value = stored != null && stored !== "" ? stored : (element.value ?? "")
    } else {
      // Editable text shows a dim placeholder when empty; the preview simply leaves it out.
      value = stored ?? ""
    }
  }
  return displayText({ text: value, style: element.style })
}

/**
 * The text as the face shows it: Title Case when the element's style says
 * `textTransform: 'capitalize'` (CSS does that on the DOM), otherwise exactly as given. Display
 * only — the line item properties never see this.
 */
function displayText({ text, style }: { text: string; style: unknown }): string {
  const textTransform = (style as Record<string, unknown> | undefined)?.textTransform
  return textTransform === "capitalize" ? capitaliseWords(text) : text
}

/**
 * Draws one TEXT element.
 *
 * The whole measure-and-draw pass runs inside one `save`/`restore`: measuring changes `font` and
 * `letterSpacing` on the shared context, and a title with `letter-spacing: 0.08em` was leaking its
 * tracking into every element drawn after it — the months list came out spaced like a headline.
 *
 * Exported for tests: the drawing is asserted against a recording stub, since jsdom has no canvas.
 *
 * @param options - Options object.
 * @param options.draw - The shared canvas context and face geometry.
 * @param options.element - The TEXT element to draw.
 */
export function drawTextElement({
  draw,
  element,
}: {
  draw: DrawContext
  element: Extract<UVInsertFaceElementConfigData, { type: "TEXT" }>
}): void {
  const { ctx, faceWidth, faceHeight, textByKey } = draw
  const text = resolveTextValue({ element, textByKey })
  if (!text.trim()) return

  ctx.save()
  try {
    drawTextElementInner({ draw, element, text })
  } finally {
    ctx.restore()
  }
}

function drawTextElementInner({
  draw,
  element,
  text,
}: {
  draw: DrawContext
  element: Extract<UVInsertFaceElementConfigData, { type: "TEXT" }>
  text: string
}): void {
  const { ctx, faceWidth, faceHeight } = draw

  const box = resolveElementBox({
    size: element.size,
    positioning: element.positioning,
    faceWidth,
    faceHeight,
  })
  const baseFontSize = resolveFontSizePx(element.fontSize)
  const lineHeight = element.lineHeight ?? 1.2
  const style = (element.style ?? {}) as Record<string, string | number>
  const letterSpacingEm = /^(-?\d*\.?\d+)em$/.exec(String(style.letterSpacing ?? ""))

  const applyFont = (size: number) => {
    ctx.font = buildCanvasFont({ font: element.font, fontSize: size, fontWeight: element.fontWeight })
    setCanvasLetterSpacing({
      ctx,
      letterSpacing: letterSpacingEm ? `${Number.parseFloat(letterSpacingEm[1]) * size}px` : "0px",
    })
  }

  // The DOM textarea carries `getDefaultTextBoxPadding(font)` — em on all four sides for Amalfi
  // Coast, none otherwise — so its content box is narrower than the element box and its text sits
  // in from the top and the sides. Mirror all of it at the drawn size: the wrap width decides the
  // line count, which decides the shrink, which decides the size the padding is measured at.
  const paddingEm = getTextBoxPaddingEm(element.font)
  const contentWidth = (size: number) => Math.max(0, box.width - (paddingEm.left + paddingEm.right) * size)

  const countLines = (size: number) => {
    applyFont(size)
    return wrapText({
      text,
      maxWidth: contentWidth(size),
      measure: (s) => ctx.measureText(s).width,
      breakLongWords: true,
    }).length
  }

  const fontSize = shrinkFontSizeToFit({
    fontSize: baseFontSize,
    minFontSize: element.minFontSize,
    lineHeight,
    maxHeight: box.maxHeight,
    maxLines: element.maxLines,
    countLines,
    verticalPaddingEm: paddingEm.top + paddingEm.bottom,
  })

  applyFont(fontSize)
  const lines = wrapText({
    text,
    maxWidth: contentWidth(fontSize),
    measure: (s) => ctx.measureText(s).width,
    breakLongWords: true,
  })
  if (lines.length === 0) return

  const align = String(style.textAlign ?? DEFAULT_TEXT_ALIGN)
  const x =
    align === "left"
      ? box.left + paddingEm.left * fontSize
      : align === "right"
        ? box.left + box.width - paddingEm.right * fontSize
        : box.left + box.width / 2
  const topPadding = paddingEm.top * fontSize
  const lineBox = lineHeight * fontSize

  ctx.textAlign = align === "left" ? "left" : align === "right" ? "right" : "center"
  // The DOM centres the glyphs in their line box; `middle` is the closest canvas equivalent and
  // spares the preview a font-metrics calculation it does not need to be right about.
  ctx.textBaseline = "middle"
  ctx.fillStyle = resolveColor(element.color, "#000000")
  lines.forEach((line, index) => {
    if (!line) return
    ctx.fillText(line, x, box.top + topPadding + (index + 0.5) * lineBox)
  })
}

/**
 * Draws one TEXT_LIST element.
 *
 * Exported for tests: the drawing is asserted against a recording stub, since jsdom has no canvas.
 *
 * @param options - Options object.
 * @param options.draw - The shared canvas context and face geometry.
 * @param options.element - The TEXT_LIST element to draw.
 */
export function drawTextListElement({
  draw,
  element,
}: {
  draw: DrawContext
  element: UVInsertFaceElementTextListConfig
}): void {
  const { ctx, faceWidth, faceHeight, textByKey } = draw
  const rows = getCardPreviewTextListRows({ element, textByKey })
  if (rows.length === 0) return

  const box = resolveElementBox({
    size: element.size,
    positioning: element.positioning,
    faceWidth,
    faceHeight,
  })
  const fontSize = resolveFontSizePx(element.fontSize)
  const lineHeight = element.lineHeight ?? 1.2
  const lineBox = lineHeight * fontSize
  const color = resolveColor(element.color, "#000000")

  const rightAligned = element.align === "right"
  // The rule (when the element declares one) is drawn from the rows this pass actually lays out,
  // so it flexes with them; its metrics come from the same builder the DOM list reads.
  const rule = getUvInsertTextListRuleMetrics({ element })
  const reservedWidth = rule?.reservedWidth ?? 0
  const overhang = rule?.overhang ?? 0

  ctx.save()
  ctx.textAlign = rightAligned ? "right" : "left"
  ctx.textBaseline = "middle"
  // The list has no tracking of its own; say so rather than inheriting whatever drew last.
  setCanvasLetterSpacing({ ctx, letterSpacing: "0px" })

  // `.visualiser-text-list` is `overflow: hidden`. It matters here and nowhere else: `wrapText`
  // leaves a word wider than the column to overflow rather than splitting it, so one long unbroken
  // moment would otherwise run out of its 76px column and across the polaroid beside it. The TEXT
  // painters are deliberately left unclipped — their boxes are now sized to the line box itself, and
  // a canvas `middle` baseline puts ascenders and descenders right on the edge, so clipping them
  // would shave glyphs the DOM shows.
  const clipHeight = box.height ?? box.maxHeight
  if (clipHeight != null && clipHeight > 0) {
    ctx.beginPath()
    ctx.rect(box.left, box.top, box.width, clipHeight)
    ctx.clip()
  }

  const headlineFont = buildCanvasFont({ font: element.font, fontSize, fontWeight: 600 })
  // `.visualiser-text-list__moment` carries no weight or style of its own, so it inherits the
  // list's — the v2 card dropped the italic the first version printed.
  const momentFont = buildCanvasFont({ font: element.font, fontSize, fontWeight: element.fontWeight })
  const bulletFont = momentFont
  // Star mode draws a polygon, not a glyph, so the text column gives up no room to a glyph.
  const star = getUvInsertTextListStarMetrics({ element })
  ctx.font = bulletFont
  const bulletWidth = !star && element.bullet.glyph ? ctx.measureText(element.bullet.glyph).width : 0
  const bulletGap = star ? 0 : TEXT_LIST_BULLET_GAP_EM * fontSize
  // Left mode measures the text column from the left edge, past the bullet. Right mode anchors
  // each line at the element's right edge instead, but keeps the *same* column width so a row
  // wraps identically whichever way it is aligned.
  // The rule takes `reservedWidth` out of the box on the anchor side, exactly as the DOM pads the
  // `<ul>`: in right mode the content keeps its left edge and loses width; in left mode it starts
  // that much further in. Either way the text column shrinks by the same amount, so a moment wraps
  // identically in both renderers. Both terms are 0 for an element with no rule.
  const contentLeft = rightAligned ? box.left : box.left + reservedWidth
  const contentWidth = Math.max(0, box.width - reservedWidth)
  const linesLeft = contentLeft + bulletWidth + bulletGap
  const linesWidth = Math.max(0, contentWidth - (bulletWidth + bulletGap))
  const textX = rightAligned ? contentLeft + contentWidth : linesLeft
  // In star mode the headline shares its line with the star, so it wraps `size + gap` narrower and
  // (left-aligned) starts that far in; the moment below keeps the full column.
  const starRoom = star ? star.size + star.gap : 0
  const headlineWidthLimit = Math.max(0, contentWidth - starRoom)
  const headlineX = rightAligned || !star ? textX : contentLeft + starRoom
  const momentX = star && !rightAligned ? contentLeft : textX
  // A fixed pitch replaces the row gap, exactly as the DOM list does.
  const rowGap = element.rowPitch !== undefined ? 0 : element.rowGap
  // Space between the headline and the first moment line when the element sets a `momentOffset`.
  const momentMargin = getUvInsertTextListMomentMargin({ element })

  const rowsTop = box.top + overhang
  let y = rowsTop
  rows.forEach((row, rowIndex) => {
    const headlineText = displayText({ text: row.headline, style: element.style })
    const momentText = displayText({ text: row.moment, style: element.style })
    ctx.font = headlineFont
    const headlineLines = wrapText({
      text: headlineText,
      maxWidth: star ? headlineWidthLimit : linesWidth,
      measure: (s) => ctx.measureText(s).width,
    })
    const headlineWidth = Math.max(0, ...headlineLines.map((line) => ctx.measureText(line).width))
    ctx.font = momentFont
    const momentLines = momentText
      ? wrapText({
          text: momentText,
          maxWidth: star && !rightAligned ? contentWidth : linesWidth,
          measure: (s) => ctx.measureText(s).width,
        })
      : []
    const momentWidth = Math.max(0, ...momentLines.map((line) => ctx.measureText(line).width))
    // The bullet hangs off the widest line of the row, not off the headline: on this card the
    // moment is usually the longer of the two, and anchoring off the headline would drop the star
    // inside the text.
    const rowTextWidth = Math.max(headlineWidth, momentWidth)

    // Star mode: the first headline line is as tall as the star if the text line is shorter, and
    // the star and that line share its centre. Without a star it is an ordinary line box.
    const firstLineBox = star ? star.lineHeightPx : lineBox
    const firstLineCentre = y + firstLineBox / 2

    if (star) {
      // The star travels with the headline, not the moment: its right tip sits `gap` left of the
      // headline's first letter, so a short month keeps its star close even under a long moment.
      const rightTip = rightAligned ? headlineX - headlineWidth - star.gap : contentLeft + star.size
      const centreX = rightTip - star.size / 2
      const points = getBirthstoneStarPoints({ cx: centreX, cy: firstLineCentre, size: star.size })
      ctx.beginPath()
      points.forEach(([pointX, pointY], pointIndex) => {
        if (pointIndex === 0) ctx.moveTo(pointX, pointY)
        else ctx.lineTo(pointX, pointY)
      })
      ctx.closePath()
      ctx.fillStyle = star.fillColor
      ctx.fill()
      ctx.strokeStyle = star.outlineColor
      ctx.lineWidth = star.outlineWidth
      ctx.lineJoin = "round"
      ctx.stroke()
      ctx.fillStyle = resolveColor(row.bulletColor, star.fillColor)
      ctx.beginPath()
      ctx.arc(centreX, firstLineCentre, star.circleRadius, 0, Math.PI * 2)
      ctx.fill()
    } else if (element.bullet.glyph) {
      ctx.font = bulletFont
      ctx.fillStyle = resolveColor(row.bulletColor, color)
      if (rightAligned) {
        // `textAlign: 'right'` would put the glyph's *right* edge on the x we pass; draw it from
        // its own left edge instead, then hand the anchor back for the text.
        ctx.textAlign = "left"
        ctx.fillText(element.bullet.glyph, textX - rowTextWidth - bulletGap - bulletWidth, y + lineBox / 2)
        ctx.textAlign = "right"
      } else {
        ctx.fillText(element.bullet.glyph, contentLeft, y + lineBox / 2)
      }
    }

    ctx.fillStyle = color
    // Line centres, top to bottom; only the very first line of a star row can be taller than a
    // plain line box, so everything after it is offset by the difference.
    const extraFirstLine = firstLineBox - lineBox
    let lineIndex = 0
    ctx.font = headlineFont
    for (const line of headlineLines) {
      ctx.fillText(
        line,
        headlineX,
        lineIndex === 0 ? firstLineCentre : y + extraFirstLine + (lineIndex + 0.5) * lineBox,
      )
      lineIndex += 1
    }
    ctx.font = momentFont
    for (const line of momentLines) {
      ctx.fillText(line, momentX, y + extraFirstLine + momentMargin + (lineIndex + 0.5) * lineBox)
      lineIndex += 1
    }

    const rowHeight =
      lineIndex * lineBox + (lineIndex > 0 ? extraFirstLine : 0) + (momentLines.length > 0 ? momentMargin : 0)
    y = element.rowPitch !== undefined ? rowsTop + (rowIndex + 1) * element.rowPitch : y + rowHeight + rowGap
  })

  if (rule) {
    // `y` has one trailing row gap on it; the rule spans the rows themselves plus an overhang at
    // each end. Nothing here knows the row count — take the length away and the flex goes with it.
    const contentHeight = y - rowGap - rowsTop
    const ruleHeight = contentHeight + 2 * overhang
    const centreX = rightAligned
      ? box.left + box.width - rule.stripWidth / 2
      : box.left + rule.stripWidth / 2

    ctx.fillStyle = resolveColor(rule.color, color)
    ctx.fillRect(centreX - rule.lineWidth / 2, box.top, rule.lineWidth, ruleHeight)
    if (rule.dotRadius > 0) {
      for (const centreY of [box.top + rule.dotRadius, box.top + ruleHeight - rule.dotRadius]) {
        ctx.beginPath()
        ctx.arc(centreX, centreY, rule.dotRadius, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }

  ctx.restore()
}

/** Draws one IMAGE_UPLOAD element, including its CSS border frame. */
async function drawImageElement({
  draw,
  element,
}: {
  draw: DrawContext
  element: Extract<UVInsertFaceElementConfigData, { type: "IMAGE_UPLOAD" }>
}): Promise<void> {
  const { ctx, faceWidth, faceHeight, imageByKey } = draw
  const box = resolveElementBox({
    size: element.size,
    positioning: element.positioning,
    faceWidth,
    faceHeight,
  })
  const width = box.width
  const height = box.height ?? box.maxHeight
  if (!(width > 0) || height == null || !(height > 0)) return

  const style = (element.style ?? {}) as Record<string, string | number>
  const border = parseSolidBorder(style.border)
  if (border) {
    ctx.save()
    ctx.fillStyle = resolveColor(border.color, "#ffffff")
    ctx.fillRect(
      box.left - border.width,
      box.top - border.width,
      width + border.width * 2,
      height + border.width * 2,
    )
    ctx.restore()
  }

  ctx.save()
  ctx.fillStyle = resolveColor(element.backgroundColor, "#ffffff")
  ctx.fillRect(box.left, box.top, width, height)
  ctx.restore()

  const file = imageByKey[element.lineItemPropertyKey]
  if (!(file instanceof File)) return

  let objectUrl: string | null = null
  try {
    const { image, url } = await loadImageFromFile(file)
    objectUrl = url
    const rect = computeCoverFitRect({
      naturalWidth: image.naturalWidth || image.width,
      naturalHeight: image.naturalHeight || image.height,
      boxWidth: width,
      boxHeight: height,
    })
    if (!rect) return
    ctx.save()
    ctx.beginPath()
    ctx.rect(box.left, box.top, width, height)
    ctx.clip()
    ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, box.left, box.top, width, height)
    ctx.restore()
  } catch (error) {
    // A photo that will not decode costs its own box, not the whole card.
    console.warn("renderCardPreview: skipping an image that could not be drawn", error)
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}

export type RenderCardPreviewOptions = {
  /** The active UV insert config, exactly as the DOM renderer receives it. */
  config: UVInsertConfig | null | undefined
  /** Which face to rasterise. Defaults to the first, which is the only face the card has. */
  faceIndex?: number
  /** The slot's text state, derived keys included. */
  textByKey: Record<string, string>
  /** The slot's image state — pass the crops the cart will receive, not the raw uploads. */
  imageByKey: Record<string, File | null>
  /** Selected theme variant image, drawn as the FRONT face background (mirrors the DOM). */
  variantImageUrl?: string | null
  /** Rasterised width in pixels; the height follows the face's aspect ratio. */
  outputWidth?: number
  /** Name for the produced file. */
  fileName?: string
}

/**
 * Rasterises a UV insert face to a JPEG `File`.
 *
 * Returns `null` — never throws — when there is no canvas, no face to draw, or the encode fails.
 * The caller is expected to simply omit the preview in that case; add to bag must not depend on it.
 *
 * @param options - The config, the face, the customer's content and the output size.
 * @returns The rasterised card, or null when it could not be produced.
 */
export async function renderCardPreview({
  config,
  faceIndex = 0,
  textByKey,
  imageByKey,
  variantImageUrl,
  outputWidth = DEFAULT_OUTPUT_WIDTH,
  fileName = "keepsake-card.jpg",
}: RenderCardPreviewOptions): Promise<File | null> {
  try {
    if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") return null

    const face: UVInsertFaceElement | undefined = config?.faceElements?.[faceIndex]
    if (!face) return null

    const faceSize = UV_INSERT_FACE_SIZES[face.type] ?? UV_INSERT_FACE_SIZES.INSERT
    const width = Math.max(1, Math.round(outputWidth))
    const height = Math.max(1, Math.round((width * faceSize.height) / faceSize.width))

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")
    if (!ctx) return null

    // Custom faces (Romie, Amalfi Coast) silently fall back to a system font when the canvas draws
    // before they have loaded, and there is no repaint to correct it afterwards. Nothing on the
    // page may have painted this face's fonts yet, so explicitly request them before waiting.
    await ensureFaceFontsLoaded({ face })

    // Lay out in face CSS pixels — the coordinate space the config is authored in — and scale once.
    ctx.scale(width / faceSize.width, height / faceSize.height)

    ctx.fillStyle = resolveColor(face.backgroundColor, "#ffffff")
    ctx.fillRect(0, 0, faceSize.width, faceSize.height)

    // Mirrors `DevFaceVisualisers`: the selected variant image backs the FRONT face when there is one.
    if (face.location === "FRONT" && variantImageUrl) {
      try {
        const background = await loadImageFromUrl(variantImageUrl)
        const rect = computeCoverFitRect({
          naturalWidth: background.naturalWidth || background.width,
          naturalHeight: background.naturalHeight || background.height,
          boxWidth: faceSize.width,
          boxHeight: faceSize.height,
        })
        if (rect) {
          ctx.drawImage(background, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, faceSize.width, faceSize.height)
        }
      } catch (error) {
        // A blocked or tainted CDN image leaves the solid background — better than no preview.
        console.warn("renderCardPreview: drawing the face background failed", error)
      }
    }

    // `.visualiser` is `overflow: hidden`; anything the config pushes past the edge is cut, not kept.
    ctx.beginPath()
    ctx.rect(0, 0, faceSize.width, faceSize.height)
    ctx.clip()

    const draw: DrawContext = {
      ctx,
      faceWidth: faceSize.width,
      faceHeight: faceSize.height,
      textByKey: textByKey ?? {},
      imageByKey: imageByKey ?? {},
    }

    for (const element of face.elementConfig ?? []) {
      if (element.type === "TEXT") {
        drawTextElement({ draw, element })
        continue
      }
      if (isUvInsertTextListElement(element)) {
        drawTextListElement({ draw, element })
        continue
      }
      if (element.type === "IMAGE_UPLOAD") {
        // Sequential rather than parallel: the elements share one canvas and one draw order.
        await drawImageElement({ draw, element })
      }
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", OUTPUT_QUALITY)
    })
    if (!blob) return null

    return new File([blob], fileName, { type: "image/jpeg" })
  } catch (error) {
    // Add to bag must never depend on this picture existing.
    console.warn("renderCardPreview: no preview produced", error)
    return null
  }
}
